import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { routeLabel } from '../../routeLabels';
import { lumioPageActions, type PageAction } from './endpoint';

/**
 * "On this page" — the route-aware chips in the Lumio briefing. Each studio area
 * can do specific things (Operations creates promotions, Acquisition runs
 * campaigns…); these chips surface that so the panel reads as "Lumio knows where
 * you are". A chip's `label` is what the owner sees; picking it sends `message`
 * as a real chat turn, so the answer threads normally.
 *
 * The copy is BACKEND-owned and localized (lumio `proactive_copy.py`, `GET
 * /assistant/page-actions`) — the frontend holds none of it. We fetch the whole
 * section → chips map ONCE per locale, cache it, and pick the current section by
 * `routeLabel` (the same closed vocabulary the section tag and breadcrumb use).
 * So navigating between pages is instant (no round-trip, no empty flash), only
 * the first visit in a locale hits the network, and a section with nothing to
 * offer simply isn't in the map → `[]` → the briefing omits the block.
 */
export type { PageAction };

// Module-level cache so every panel instance / remount and every navigation
// reuses one fetch per locale. Keyed by the exact i18n language code.
const sectionsByLocale = new Map<string, Record<string, PageAction[]>>();

export const usePageActions = (pathname: string): PageAction[] => {
  const { i18n } = useTranslation();
  const locale = i18n.language || 'en';
  const [sections, setSections] = useState<Record<string, PageAction[]>>(
    () => sectionsByLocale.get(locale) ?? {},
  );

  useEffect(() => {
    const cached = sectionsByLocale.get(locale);
    if (cached) {
      setSections(cached);
      return undefined;
    }
    const controller = new AbortController();
    lumioPageActions(locale, controller.signal)
      .then((map) => {
        sectionsByLocale.set(locale, map);
        if (!controller.signal.aborted) setSections(map);
      })
      .catch(() => {
        /* best-effort: on failure the briefing simply shows no page chips */
      });
    return () => controller.abort();
  }, [locale]);

  return sections[routeLabel(pathname)] ?? [];
};
